/*
__/\\\\\\\\\\\\\\\__/\\\\\\\\\\\\\\\_____/\\\\\\\\\____        
 _\///////\\\/////__\///////\\\/////____/\\\\\\\\\\\\\__       
  _______\/\\\_____________\/\\\________/\\\/////////\\\_      
   _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_     
    _______\/\\\_____________\/\\\_______\/\\\\\\\\\\\\\\\_    
     _______\/\\\_____________\/\\\_______\/\\\/////////\\\_   
      _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_  
       _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_ 
        _______\///______________\///________\///________\///__
            
            COPYRIGHT TACTICAL TRANSPORTATION ADVISORS, INC. 
            ALL RIGHTS RESERVED.
*/

import React, { useEffect, useState } from "react";
import { Button, Dropdown, Form, InputGroup, Modal, Pagination } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBriefcase, faFilter, faMagnifyingGlass, faShield } from "@fortawesome/free-solid-svg-icons";
import QuickTable from "../../../components/QuickTable";
import CustomButton from "../../../components/CustomButton";
import LoadingWrapper from "../../../components/LoadingWrapper";
import DocumentRequestElement from "./DocumentRequestElement";
import DocumentRequestDetailsOnboarding from "./DocumentRequestDetailsOnboarding";
import { getHistoricalOnboarding } from "../../../services/DashboardService";
import { useStateWithCookies } from "../../../components/useStateWithCookies";
import { DirectDepositDocuments, reactPdfToBase64, taxDocumentDictionary } from "../../../tools";
import PDFMerger from "pdf-merger-js";
import { Document, Image, Page, Text } from "@react-pdf/renderer";
import { downloadDocumentRequestOnboarding } from "../../../services/HRDocumentsService";
import moment from "moment";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function DashboardTC2Historical() {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filterType, setFilterType] = useState("Company");
  const [filterValue, setFilterValue] = useState("");
  const [debouncedFilterValue, setDebouncedFilterValue] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedCompanyIdentifiers, setSelectedCompanyIdentifiers] = useStateWithCookies("old-admin-hist-tc2-excluded-companies", []);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [pendingEVerify, setPendingEVerify] = useState(false);
  const [pendingPaychex, setPendingPaychex] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  ////////////////
  /// Functions
  ////////////////

  const loadData = async (pg, ps, filters) => {
    setIsLoading(true);
    const response = await getHistoricalOnboarding({ page: pg, pageSize: ps, ...filters });
    if (response?.status === "200") {
      setRows(response.rows ?? []);
      setTotalCount(response.totalCount ?? 0);
      if (response.companies?.length) {
        setCompanies(response.companies);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedFilterValue(filterValue);
    }, 500);

    return () => clearTimeout(timeout);
  }, [filterValue]);

  useEffect(() => {
    loadData(page, pageSize, {
      filterType,
      filterValue: debouncedFilterValue,
      dateFrom,
      dateTo,
      excludedCompanyIdentifiers: selectedCompanyIdentifiers,
      pendingEVerify,
      pendingPaychex,
    });
  }, [page, pageSize, filterType, debouncedFilterValue, dateFrom, dateTo, selectedCompanyIdentifiers, pendingEVerify, pendingPaychex]);

  const handleClearFilters = () => {
    setPage(1);
    setFilterType("Company");
    setFilterValue("");
    setDateFrom("");
    setDateTo("");
    setSelectedCompanyIdentifiers([]);
    setPendingEVerify(false);
    setPendingPaychex(false);
  };

  const toggleCompany = (uid) => {
    setPage(1);
    setSelectedCompanyIdentifiers((prev) => (prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]));
  };

  async function handleDownloadDocumentRequest(documentRequest, setIsDownloading) {
    setIsDownloading(documentRequest.uid);
    const response = await downloadDocumentRequestOnboarding(documentRequest.uid);
    if (response.status == 200) {
      const merger = new PDFMerger();

      const sortedDocumentTypes = ["offerLetter", "i9", "w4", "dl", "ssn", "healthDental", "vision", ...Object.keys(taxDocumentDictionary), ...Object.keys(DirectDepositDocuments), "f8850"].filter(
        (d) => Object.keys(response.documents).includes(d),
      );

      for (let i = 0; i < sortedDocumentTypes.length; i++) {
        const document = response.documents[sortedDocumentTypes[i]];
        if (document?.base64) {
          if (!document?.base64.split(",", 1)[0].includes("pdf")) {
            const pdf = (
              <Document>
                <Page size="LETTER" style={{ position: "relative", padding: 50 }}>
                  <Text
                    style={{
                      fontSize: 15,
                      textAlign: "center",
                      marginBottom: 12,
                    }}
                  >
                    {sortedDocumentTypes[i] === "dl" ? "Driver's License" : sortedDocumentTypes[i] === "ssn" ? "Social Security Card" : ""}
                  </Text>
                  <Image style={{ objectFit: "contain", maxHeight: 600 }} src={document.base64} />
                </Page>
              </Document>
            );
            const base64 = await reactPdfToBase64(pdf);
            const fetchResponse = await fetch(base64);
            const blob = await fetchResponse.blob();
            await merger.add(blob);
          } else {
            const fetchResponse = await fetch(document.base64);
            const blob = await fetchResponse.blob();
            await merger.add(blob);
          }
        }
      }

      const mergedPdf = await merger.saveAsBlob();

      const elem = window.document.createElement("a");
      elem.target = "_blank";
      elem.href = window.URL.createObjectURL(mergedPdf);
      elem.download = `${documentRequest.employee?.firstName ?? ""} ${documentRequest.employee?.lastName ?? ""} Document Request ${moment(documentRequest.dateCreated).format("MMM D, YYYY")}`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    }
    setIsDownloading(undefined);
  }

  /////////////

  const filterTypeItems = ["Company", "CSA", "Employee"].map((type) => (
    <Dropdown.Item
      key={type}
      active={filterType === type}
      onClick={() => {
        setPage(1);
        setFilterType(type);
      }}
    >
      {type}
    </Dropdown.Item>
  ));

  const pageSizeItems = PAGE_SIZE_OPTIONS.map((size) => (
    <Dropdown.Item
      key={size}
      active={pageSize === size}
      onClick={() => {
        setPageSize(size);
        setPage(1);
      }}
    >
      {size} per page
    </Dropdown.Item>
  ));

  const visibleCount = companies.length - selectedCompanyIdentifiers.length;

  const tableRows = rows.map((request) => (
    <DocumentRequestElement
      key={request.uid}
      documentRequest={request}
      isHistorical
      showStatus
      handleShowModal={(req) => {
        setSelectedRequest(req);
        setShowDetailsModal(true);
      }}
      handleDownloadDocumentRequest={handleDownloadDocumentRequest}
      handleDismissRequest={() => {}}
      onEVerifySuccess={() => {
        loadData(page, pageSize, {
          filterType,
          filterValue: debouncedFilterValue,
          dateFrom,
          dateTo,
          excludedCompanyIdentifiers: selectedCompanyIdentifiers,
          pendingEVerify,
          pendingPaychex,
        });
      }}
    />
  ));

  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <InputGroup style={{ flex: "1 1 280px", minWidth: 280 }}>
          <InputGroup.Text>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </InputGroup.Text>
          <Form.Control
            placeholder={`Filter by ${filterType}`}
            autoComplete="off"
            value={filterValue}
            onChange={(e) => {
              setPage(1);
              setFilterValue(e.target.value);
            }}
          />
          <Dropdown>
            <Dropdown.Toggle>{filterType}</Dropdown.Toggle>
            <Dropdown.Menu>{filterTypeItems}</Dropdown.Menu>
          </Dropdown>
        </InputGroup>

        <Dropdown>
          <Dropdown.Toggle variant="outline-secondary">{companies.length === 0 ? "Select Companies" : `${visibleCount}/${companies.length} Companies`}</Dropdown.Toggle>
          <Dropdown.Menu style={{ maxHeight: 200, overflowY: "auto" }}>
            {[...companies]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((company) => (
                <Dropdown.Item
                  key={company.uid}
                  active={!selectedCompanyIdentifiers.includes(company.uid)}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCompany(company.uid);
                  }}
                >
                  {company.name}
                </Dropdown.Item>
              ))}
          </Dropdown.Menu>
        </Dropdown>

        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Form.Label style={{ margin: 0, fontSize: 14 }}>From</Form.Label>
            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setPage(1);
                setDateFrom(e.target.value);
              }}
              style={{ width: 150 }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Form.Label style={{ margin: 0, fontSize: 14 }}>To</Form.Label>
            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => {
                setPage(1);
                setDateTo(e.target.value);
              }}
              style={{ width: 150 }}
            />
          </div>
        </div>

        <Dropdown>
          <Dropdown.Toggle variant={pendingEVerify || pendingPaychex ? "primary" : "outline-secondary"}>
            <FontAwesomeIcon icon={faFilter} className="me-1" />
            Status Filters
            {(pendingEVerify || pendingPaychex) && (
              <span className="badge bg-light text-dark ms-2">
                {[pendingEVerify, pendingPaychex].filter(Boolean).length}
              </span>
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item
              active={pendingEVerify}
              onClick={(e) => { e.stopPropagation(); setPage(1); setPendingEVerify((v) => !v); }}
            >
              <FontAwesomeIcon icon={faShield} className="me-2" />
              Pending E-Verify
            </Dropdown.Item>
            <Dropdown.Item
              active={pendingPaychex}
              onClick={(e) => { e.stopPropagation(); setPage(1); setPendingPaychex((v) => !v); }}
            >
              <FontAwesomeIcon icon={faBriefcase} className="me-2" />
              Pending Paychex
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <div style={{ display: "flex", gap: 8 }}>
          <CustomButton label="Clear" onClick={handleClearFilters} style={{ backgroundColor: "#6c757d" }} />
        </div>
      </div>

      <LoadingWrapper isLoading={isLoading}>
        <QuickTable
          title={`Historical Document Requests (${totalCount})`}
          headers={["Company", "CSA", "State", "Employee", "Date Created", "Date Dismissed", "Documents Requested", "Paychex Worker", "Status", ""]}
          rows={tableRows}
          widths={[null, null, null, null, 1, null, null, 1, 1, 1]}
          noWrap
          size="sm"
        />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: 12,
            flexWrap: "wrap",
            gap: 12,
            width: "100%",
          }}
        >
          <span style={{ fontSize: 13, color: "#6c757d", whiteSpace: "nowrap" }}>
            {totalCount === 0 ? "0 results" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount} results`}
          </span>

          <Pagination style={{ margin: 0 }}>
            <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />

            <Pagination.Prev disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => Math.abs(p - page) <= 2 || p === 1 || p === totalPages)
              .map((p, i, arr) => (
                <React.Fragment key={p}>
                  {i > 0 && arr[i - 1] + 1 !== p && <Pagination.Ellipsis disabled />}
                  <Pagination.Item active={p === page} onClick={() => setPage(p)}>
                    {p}
                  </Pagination.Item>
                </React.Fragment>
              ))}

            <Pagination.Next disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />

            <Pagination.Last disabled={page === totalPages} onClick={() => setPage(totalPages)} />
          </Pagination>

          <div>
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" size="sm">
                {pageSize} per page
              </Dropdown.Toggle>
              <Dropdown.Menu>{pageSizeItems}</Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </LoadingWrapper>
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="xl">
        {selectedRequest && <DocumentRequestDetailsOnboarding documentRequest={selectedRequest} handleRemoveDocumentRequestDocument={() => {}} handleSetDocumentRequestDocumentIncomplete={() => {}} />}
      </Modal>
    </>
  );
}
